import { useCallback, useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import axios from "axios";
import { Sparkles } from "lucide-react";
import { SparkleBurst, ConfettiRain, Fireworks } from "@/Celebrations";
import { BreathingGuide } from "@/Breathing";
import { MoonCountdown } from "@/MoonCountdown";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const WishForm = ({ sessionId, done, onDone }) => {
  const [wish, setWish] = useState("");
  const [sending, setSending] = useState(false);
  const [error, setError] = useState("");

  if (done) {
    return <p className="wish-sent" data-testid="wish-sent-message">Your dream has been sent into the universe. It is on its way to you. A copy of your wish is on its way to your email.</p>;
  }

  const submit = async (e) => {
    e.preventDefault();
    if (wish.trim().length < 3) return setError("Describe your dream in a few words.");
    setSending(true);
    setError("");
    try {
      await axios.post(`${API}/wishes`, { session_id: sessionId, wish: wish.trim() });
      onDone();
    } catch {
      setError("Your wish could not be sent. Please try again.");
      setSending(false);
    }
  };

  return (
    <form className="wish-form" onSubmit={submit} data-testid="wish-form">
      <label htmlFor="wish" className="wish-label">Write down your dream, as if it has already come true</label>
      <textarea
        id="wish"
        value={wish}
        onChange={(e) => setWish(e.target.value)}
        maxLength={2000}
        rows={4}
        placeholder="I am so grateful now that…"
        className="wish-input"
        data-testid="wish-input"
      />
      {error && <p className="dream-error" data-testid="wish-error">{error}</p>}
      <button type="submit" className="dream-btn wish-submit" disabled={sending} data-testid="wish-submit-button">
        {sending ? "Sending to the stars…" : "Send my dream to the universe"}
      </button>
    </form>
  );
};

export const DreamButton = ({ visible }) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const sessionRef = useRef(null);
  const createSession = () =>
    axios.post(`${API}/payments/checkout`, { origin_url: window.location.origin }).then(({ data }) => data.checkout_url);

  // Prepare the Stripe page as soon as the buttons show, so a tap opens it instantly.
  useEffect(() => {
    if (!visible || sessionRef.current) return;
    const p = createSession();
    p.catch(() => { sessionRef.current = null; });
    sessionRef.current = { p, at: Date.now() };
  }, [visible]);

  const warm = () => {
    if (sessionRef.current) return;
    const p = createSession();
    p.catch(() => { sessionRef.current = null; });
    sessionRef.current = { p, at: Date.now() };
  };

  const checkout = async () => {
    setLoading(true);
    setError("");
    const fresh = sessionRef.current && Date.now() - sessionRef.current.at < 30 * 60 * 1000;
    try {
      const url = await (fresh ? sessionRef.current.p : createSession()).catch(createSession);
      window.location.href = url;
    } catch {
      sessionRef.current = null;
      setError("The stars are misaligned. Please try again.");
      setLoading(false);
    }
  };

  return (
    <div className={`dream-cta ${visible ? "visible" : ""}`}>
      {loading && createPortal(
        <div className="portal-overlay" data-testid="checkout-loading-overlay">
          <span className="portal-spinner" />
          <p>Opening the portal…</p>
        </div>,
        document.body
      )}
      <div className="dream-btn-wrap">
        <button className="dream-btn" onPointerDown={warm} onClick={checkout} disabled={loading} data-testid="dream-checkout-button">
          {loading ? "Opening the portal…" : (
            <>Just for <strong>$7</strong>, we will make your dreams come true through the Law of Attraction</>
          )}
        </button>
      </div>
      <div className="wish-step-wrap">
        <button className="wish-step" onPointerDown={warm} onClick={checkout} disabled={loading} data-testid="wish-step-button">
          <span className="wish-step-text">
            <Sparkles size={22} className="wish-step-icon" />
            ONCE PAYMENT IS MADE YOU WILL BE ASKED TO MAKE A WISH DON&apos;T{"\u00a0"}MISS{"\u00a0"}OUT
          </span>
          <span className="click-here" data-testid="wish-step-click-here">CLICK HERE</span>
        </button>
      </div>
      {error && <p className="dream-error" data-testid="dream-checkout-error">{error}</p>}
      <p className="dream-count" data-testid="dream-count">
        <span className="dream-count-dot" /> Dreams are manifesting all over the world
      </p>
    </div>
  );
};

export const PaymentResult = ({ cancelled = false }) => {
  const [status, setStatus] = useState(cancelled ? "cancelled" : "checking");
  const [sessionId] = useState(() => new URLSearchParams(window.location.search).get("session_id"));
  const [wishDone, setWishDone] = useState(false);
  const [celebrate, setCelebrate] = useState(false);
  const [breathed, setBreathed] = useState(false);
  const finishBreathing = useCallback(() => setBreathed(true), []);

  useEffect(() => {
    if (cancelled) return;
    if (!sessionId) return setStatus("error");
    let tries = 0;
    let timer;
    const poll = async () => {
      try {
        const { data } = await axios.get(`${API}/payments/status/${sessionId}`);
        if (data.payment_status === "paid") {
          setWishDone(data.wish_submitted);
          return setStatus("paid");
        }
        if (["expired", "failed"].includes(data.status)) return setStatus("error");
      } catch { /* keep polling */ }
      if (++tries >= 15) return setStatus("timeout");
      timer = setTimeout(poll, 2000);
    };
    poll();
    return () => clearTimeout(timer);
  }, [cancelled, sessionId]);

  const copy = {
    checking: ["Aligning the stars", "Confirming your offering with the universe…"],
    paid: ["Your dream is being manifested", "The universe has heard you. Hold your desire in your heart, feel it as already yours, and watch it unfold."],
    cancelled: ["The portal has closed", "No payment was made. Return whenever you are ready."],
    error: ["Something went astray", "We couldn't confirm your payment. Please try again."],
    timeout: ["Still aligning", "Your payment is taking longer to confirm. Refresh this page in a moment."],
  }[status];

  return (
    <>
    {status === "paid" && <Fireworks />}
    {status === "paid" && <SparkleBurst />}
    {celebrate && <ConfettiRain />}
    <div className="result-card" data-testid={`payment-result-${status}`}>
      <h1 className="result-title">{copy[0]}</h1>
      <p className="result-text">{copy[1]}</p>
      {status === "paid" && !wishDone && !breathed && <BreathingGuide onDone={finishBreathing} />}
      {status === "paid" && (wishDone || breathed) && <WishForm sessionId={sessionId} done={wishDone} onDone={() => { setWishDone(true); setCelebrate(true); }} />}
      {status === "paid" && (wishDone || breathed) && <MoonCountdown />}
      {status !== "checking" && (status !== "paid" || wishDone || breathed) && (
        <a href="/" className="ctrl-btn result-home" data-testid="payment-result-home-link">Return to the light</a>
      )}
    </div>
    </>
  );
};
